import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DoorlyProLayout, { RequirePartnerAuth } from '../../components/DoorlyProLayout';
import { useDoorlyProAuth } from '../../context/DoorlyProAuthContext';
import { Icon } from '../../utils/icons';
import { US_STATES } from '../../constants/usStates';
import { showPayoutPanel } from '../../constants/partnerPayoutPanel';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export function ProfileCircle({ partner, size = 40, fontSize = 15 }) {
  const initials = [partner?.first_name?.[0], partner?.last_name?.[0]].filter(Boolean).join('').toUpperCase() || '?';
  return partner?.profile_picture_url ? (
    <img src={partner.profile_picture_url} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.4)', flexShrink: 0 }} />
  ) : (
    <div style={{ width: size, height: size, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize, fontWeight: '800', color: '#fff', flexShrink: 0 }}>
      {initials}
    </div>
  );
}

// ⚠️ `options` TURNS THIS INTO A SELECT. Added because License State was a free
// text box here while the registration form uppercased what it took — so a
// partner editing their profile could write 'Tx' and did, three times, and the
// Partners table's State filter then offered 'TX' and 'Tx' as two states.
const Field = ({ label, value, editMode, onChange, type = 'text', placeholder = '', options = null }) => (
  <div>
    <label style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</label>
    {editMode && options ? (
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit', background: '#fff' }}>
        <option value="">Select…</option>
        {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
      </select>
    ) : editMode ? (
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }} />
    ) : (
      <div style={{ fontSize: '14px', color: value ? '#111827' : '#d1d5db', padding: '10px 0', borderBottom: '1px solid #f3f4f6', fontWeight: value ? '500' : '400' }}>{value || '—'}</div>
    )}
  </div>
);

export default function DoorlyProProfile() {
  const navigate = useNavigate();
  const { token, partnerLogout } = useDoorlyProAuth();
  const fileRef = useRef(null);
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [uploadingPic, setUploadingPic] = useState(false);
  // Banking. The panel reads GET /me/banking — the FIRM's details, from
  // partner_accounts — and the editor below it renders only when the server says
  // this person may use it. The OTP state is the same flow it always was.
  const [banking, setBanking] = useState(null);

  // 🔑 LAND ON THE SECTION THE TASK NAMED. "Add banking" on the dashboard points
  // at /doorlypro/profile#payout, and a broker who arrived at the top of their own
  // profile had to hunt for the thing they had just asked for.
  //
  // ⚠️ IT WAITS FOR THE BANKING BLOCK, NOT FOR MOUNT. The browser's own hash jump
  // fires before that block exists — its content is fetched — so the anchor is
  // not in the document yet and the jump does nothing. Scrolling once `banking`
  // has arrived is the first moment the target is real.
  useEffect(() => {
    if (window.location.hash !== '#payout' || !banking) return;
    const el = document.getElementById('payout');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [banking]);
  const [otpStep, setOtpStep] = useState('locked');
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState(null);
  const [editToken, setEditToken] = useState(null);
  const [editTokenExpiry, setEditTokenExpiry] = useState(null);
  const [achForm, setAchForm] = useState({ ach_bank_name: '', ach_account_holder_name: '', ach_routing_number: '', ach_account_number: '', ach_account_type: 'checking' });
  const [achSaving, setAchSaving] = useState(false);
  const [achError, setAchError] = useState(null);
  const [achSuccess, setAchSuccess] = useState(false);
  // Company info
  const [editingCompany, setEditingCompany] = useState(false);
  const [companySaving, setCompanySaving] = useState(false);
  const [companySaved, setCompanySaved] = useState(false);
  const [companyError, setCompanyError] = useState(null);
  const [companyForm, setCompanyForm] = useState({ brokerage_name: '', brokerage_address: '', brokerage_phone: '', brokerage_ein: '' });
  // Account security — change password / change email
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwMsg, setPwMsg] = useState(null);
  const [pwSaving, setPwSaving] = useState(false);
  const [emailForm, setEmailForm] = useState({ new_email: '', current_password: '' });
  const [emailMsg, setEmailMsg] = useState(null);
  const [emailSaving, setEmailSaving] = useState(false);

  // ⚠️ THE 5-MINUTE EDIT TOKEN RELOCKS THE FORM WHEN IT EXPIRES. Leaving an
  // unlocked banking form on screen past its token means a Save that fails with
  // "verification expired" after the numbers have been typed.
  useEffect(() => {
    if (!editTokenExpiry) return;
    const ms = new Date(editTokenExpiry).getTime() - Date.now();
    if (ms <= 0) { setOtpStep('locked'); setEditToken(null); return; }
    const t = setTimeout(() => { setOtpStep('locked'); setEditToken(null); setEditTokenExpiry(null); }, ms);
    return () => clearTimeout(t);
  }, [editTokenExpiry]);

  // 🔑 ASKED OF THE SERVER, NEVER DERIVED HERE. accountBankingAuthority is the
  // same function PATCH /banking enforces with — a page that decided for itself
  // who may edit would be a second copy of the rule that moves money, and the two
  // would disagree the first time either moved.
  const loadBanking = useCallback(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/doorlypro/me/banking`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setBanking(r.data))
      // ⚠️ A FAILED LOAD MUST NOT READ AS "no firm". hasAccount:false has its own
      // copy telling them to contact Doorly, and showing it on a network error
      // would send a partner chasing a problem they do not have.
      .catch(() => setBanking({ hasAccount: true, bankingOnFile: false, canEdit: false,
        message: 'We could not load your firm\u2019s payout details just now. Please try again.' }));
  }, [token]);
  useEffect(loadBanking, [loadBanking]);

  const isHomebuilder = partner?.partner_type === 'homebuilder';
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/doorlypro/me`, { headers: authHeaders })
      .then(res => {
        const d = res.data?.partner || res.data || {};
        setPartner(d);
        setForm(d);
        // Update localStorage
        const stored = JSON.parse(localStorage.getItem('doorlypro_partner') || '{}');
        localStorage.setItem('doorlypro_partner', JSON.stringify({ ...stored, ...d }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (partner) {
      setCompanyForm({
        brokerage_name: partner.brokerage_name || '',
        brokerage_address: partner.brokerage_address || '',
        brokerage_phone: partner.brokerage_phone || '',
        brokerage_ein: partner.brokerage_ein || '',
      });
    }
  }, [partner]);

  const handleSaveCompany = async () => {
    setCompanySaving(true); setCompanyError(null);
    try {
      await axios.put(`${API_URL}/api/doorlypro/me/profile`, companyForm, { headers: authHeaders });
      setPartner(p => ({ ...p, ...companyForm }));
      setEditingCompany(false); setCompanySaved(true);
      setTimeout(() => setCompanySaved(false), 3000);
    } catch (e) { setCompanyError(e.response?.data?.error || 'Failed to save.'); }
    finally { setCompanySaving(false); }
  };

  const handleChangePassword = async () => {
    setPwMsg(null);
    if (!pwForm.current_password || !pwForm.new_password) { setPwMsg({ ok: false, text: 'Please fill in all fields.' }); return; }
    if (pwForm.new_password.length < 8) { setPwMsg({ ok: false, text: 'New password must be at least 8 characters.' }); return; }
    if (pwForm.new_password !== pwForm.confirm_password) { setPwMsg({ ok: false, text: 'New passwords do not match.' }); return; }
    setPwSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/doorlypro/me/change-password`,
        { current_password: pwForm.current_password, new_password: pwForm.new_password },
        { headers: authHeaders });
      setPwMsg({ ok: true, text: res.data?.message || 'Password changed successfully.' });
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) { setPwMsg({ ok: false, text: e.response?.data?.error || 'Failed to change password.' }); }
    finally { setPwSaving(false); }
  };

  const handleChangeEmail = async () => {
    setEmailMsg(null);
    if (!emailForm.new_email || !emailForm.current_password) { setEmailMsg({ ok: false, text: 'Please fill in all fields.' }); return; }
    setEmailSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/doorlypro/me/change-email`,
        { new_email: emailForm.new_email, current_password: emailForm.current_password },
        { headers: authHeaders });
      setEmailMsg({ ok: true, text: res.data?.message || 'Confirmation link sent to your new email.' });
      setEmailForm({ new_email: '', current_password: '' });
    } catch (e) { setEmailMsg({ ok: false, text: e.response?.data?.error || 'Failed to start email change.' }); }
    finally { setEmailSaving(false); }
  };

  const handleSave = async () => {
    setSaving(true); setSaveMsg(null);
    try {
      await axios.put(`${API_URL}/api/doorlypro/me/profile`, form, { headers: authHeaders });
      setPartner(p => ({ ...p, ...form }));
      const stored = JSON.parse(localStorage.getItem('doorlypro_partner') || '{}');
      localStorage.setItem('doorlypro_partner', JSON.stringify({ ...stored, ...form }));
      setSaveMsg('✓ Profile updated');
      setEditMode(false);
      setTimeout(() => setSaveMsg(null), 3000);
    } catch (e) { setSaveMsg('Error: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { alert('Image must be under 3MB.'); return; }
    setUploadingPic(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });
      const res = await axios.put(`${API_URL}/api/doorlypro/me/profile`, { profile_picture_url: dataUrl }, { headers: authHeaders });
      if (res.data) {
        setPartner(p => ({ ...p, profile_picture_url: dataUrl }));
        const stored = JSON.parse(localStorage.getItem('doorlypro_partner') || '{}');
        localStorage.setItem('doorlypro_partner', JSON.stringify({ ...stored, profile_picture_url: dataUrl }));
      }
    } catch (err) {
      console.error('[ProfilePic]', err.message);
      alert('Failed to upload picture: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploadingPic(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const initials = [partner?.first_name?.[0], partner?.last_name?.[0]].filter(Boolean).join('').toUpperCase() || '?';

  return (
    <RequirePartnerAuth>
      <DoorlyProLayout>
        <div style={{ maxWidth: 680, margin: '0 auto', boxSizing: 'border-box' }}>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>Loading profile...</div>
          ) : (
          <>
            {/* Profile header */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '16px', overflow: 'hidden', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ background: 'linear-gradient(135deg, #3a6ad9, #2854c0)', height: '80px' }} />
              <div style={{ padding: '0 24px 24px', marginTop: '-40px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    {partner?.profile_picture_url ? (
                      <img src={partner.profile_picture_url} alt="" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid #fff' }} />
                    ) : (
                      <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, #3a6ad9, #2854c0)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 800, color: '#fff', border: '3px solid #fff' }}>{initials}</div>
                    )}
                    <button onClick={() => fileRef.current?.click()} disabled={uploadingPic}
                      style={{ position: 'absolute', bottom: 0, right: 0, width: 26, height: 26, borderRadius: '50%', background: '#3a6ad9', border: '2px solid #fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
                    </button>
                    <input ref={fileRef} type="file" accept="image/*" onChange={handlePictureUpload} style={{ display: 'none' }} />
                  </div>
                  <div style={{ flex: 1, paddingTop: '44px' }}>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#111827' }}>{partner?.first_name} {partner?.last_name}</div>
                    <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '2px' }}>{partner?.email}</div>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '10px', background: partner?.onboarding_status === 'active' ? '#f0fdf4' : '#fff7ed', color: partner?.onboarding_status === 'active' ? '#166534' : '#92400e', border: `1px solid ${partner?.onboarding_status === 'active' ? '#bbf7d0' : '#fed7aa'}` }}>
                        {partner?.onboarding_status === 'active' ? '● Active' : '○ Pending'}
                      </span>
                      <span style={{ fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '10px', background: '#f3f4f6', color: '#374151' }}>
                        {isHomebuilder ? 'Homebuilder' : partner?.partner_type === 'loan_officer' ? 'Loan Officer' : 'Realtor'}
                      </span>
                    </div>
                  </div>
                  {!editMode && (
                    <button onClick={() => setEditMode(true)}
                      style={{ padding: '9px 18px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', alignSelf: 'flex-end', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <Icon name="settings" size={14} color="#374151" /> Edit Profile
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Profile fields */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Profile Information</h3>
                {editMode && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => { setEditMode(false); setForm(partner); setSaveMsg(null); }}
                      style={{ padding: '7px 14px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>Cancel</button>
                    <button onClick={handleSave} disabled={saving}
                      style={{ padding: '7px 18px', background: saving ? '#e5e7eb' : '#3a6ad9', color: saving ? '#9ca3af' : '#fff', border: 'none', borderRadius: '8px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '13px' }}>
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                )}
              </div>

              {saveMsg && (
                <div style={{ background: saveMsg.startsWith('✓') ? '#f0fdf4' : '#fff0f3', border: `1px solid ${saveMsg.startsWith('✓') ? '#bbf7d0' : '#fecdd3'}`, borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: saveMsg.startsWith('✓') ? '#166534' : '#ff082f', fontWeight: '600' }}>
                  {saveMsg}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <Field label="First Name" value={form.first_name || ''} editMode={editMode} onChange={v => setForm(p => ({ ...p, first_name: v }))} placeholder="Jane" />
                  <Field label="Last Name" value={form.last_name || ''} editMode={editMode} onChange={v => setForm(p => ({ ...p, last_name: v }))} placeholder="Smith" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <Field label="Email" value={form.email || ''} editMode={false} />
                  <Field label="Phone" value={form.phone || ''} editMode={editMode} onChange={v => setForm(p => ({ ...p, phone: v }))} type="tel" placeholder="(512) 000-0000" />
                </div>
                {isHomebuilder ? (
                  <>
                    <Field label="Builder / Company Name" value={form.builder_company_name || ''} editMode={editMode} onChange={v => setForm(p => ({ ...p, builder_company_name: v }))} placeholder="Cornerstone Homes" />
                    <Field label="Markets / Communities" value={form.builder_markets || ''} editMode={editMode} onChange={v => setForm(p => ({ ...p, builder_markets: v }))} placeholder="Austin TX, DFW TX" />
                  </>
                ) : (
                  <>
                    <Field label="Brokerage" value={form.brokerage || ''} editMode={editMode} onChange={v => setForm(p => ({ ...p, brokerage: v }))} placeholder="Keller Williams Realty" />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                      <Field label="License State" value={form.license_state || ''} editMode={editMode} onChange={v => setForm(p => ({ ...p, license_state: v }))} options={US_STATES} />
                      <Field label={partner?.partner_type === 'loan_officer' ? 'NMLS #' : 'License #'} value={form.license_number || ''} editMode={editMode} onChange={v => setForm(p => ({ ...p, license_number: v }))} placeholder="TX-12345678" />
                    </div>
                    {/* ⚠️ YEARS OF EXPERIENCE REMOVED. Nothing reads it — not
                        eligibility, not assignment, not any surface in DoorlyDesk
                        — so it was a question asked of every partner whose answer
                        went nowhere. The column stays; only the ask is gone, so
                        the twenty-odd rows that hold a value keep it. */}
                  </>
                )}
                <Field label="Partner Type" value={isHomebuilder ? 'Homebuilder' : partner?.partner_type === 'loan_officer' ? 'Loan Officer' : 'Realtor'} editMode={false} />
                <Field label="Member Since" value={partner?.created_at ? new Date(partner.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—'} editMode={false} />
              </div>
            </div>

            {/* ── COMPANY INFORMATION ── */}
            {!isHomebuilder && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '16px', padding: '24px', marginTop: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div>
                  <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: '800', color: '#111827' }}>Company Information</h3>
                  <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>Your brokerage or company details</p>
                </div>
                {!editingCompany && (
                  <button onClick={() => setEditingCompany(true)}
                    style={{ padding: '8px 16px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Edit</button>
                )}
              </div>

              <div style={{ background: '#edf1fb', border: '1px solid #c5d3f5', borderLeft: '4px solid #3a6ad9', borderRadius: '0 10px 10px 0', padding: '12px 16px', marginBottom: '20px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#1e3a6e', marginBottom: '4px' }}>Compliance requirement</div>
                <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.7' }}>
                  Doorly is required to pay licensed brokerages directly. Buyer fees will be sent to your brokerage's bank account on file. Your broker is responsible for distributing earned fees to you per your brokerage agreement.
                </div>
              </div>

              {!editingCompany ? (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    {[
                      // 🚨 THE FIRM'S NAME COMES FROM THE FIRM. This read
                      // partner_users.brokerage_name, which nothing has written
                      // since registration began storing the company on
                      // partner_profiles.brokerage and resolving it onto
                      // partner_accounts — three of twenty-six rows carry the
                      // legacy value, and they are the three who once typed it
                      // into this very card. A broker who supplied their company
                      // at registration opened this and found it blank.
                      //
                      // 🔑 ACCOUNT FIRST, THEN THE PROFILE, THEN THE LEGACY COLUMN.
                      // A firm's name belongs to the firm — the same rule the W-9,
                      // the banking and the payee contact already follow — and the
                      // fallbacks keep a partner with no account yet from seeing a
                      // blank where they have an answer.
                      { label: 'Brokerage / Company Name',
                        value: partner?.account_company_name || partner?.brokerage || partner?.brokerage_name },
                      { label: 'Brokerage Address', value: partner?.brokerage_address },
                      // ⚠️ THE FIRM'S NUMBER, NOT THE PARTNER'S. The phone given at
                      // registration is the person's own and is shown under their
                      // own details above; filling this with it would state that
                      // the firm's line is their mobile. It reads "Not provided"
                      // because it genuinely has not been.
                      { label: 'Brokerage Phone', value: partner?.brokerage_phone },
                      { label: 'EIN (optional)', value: partner?.brokerage_ein },
                    ].map(item => (
                      <div key={item.label}>
                        <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '600', marginBottom: '3px' }}>{item.label}</div>
                        <div style={{ fontSize: '14px', color: item.value ? '#111827' : '#d1d5db', fontWeight: item.value ? '500' : '400' }}>{item.value || 'Not provided'}</div>
                      </div>
                    ))}
                  </div>
                  {companySaved && (
                    <div style={{ marginTop: '12px', padding: '10px 14px', background: '#f4fee8', border: '1px solid #d4f99a', borderRadius: '8px', fontSize: '13px', color: '#2d6a0a', fontWeight: '600' }}>
                      ✓ Company information saved successfully
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {partner?.account_company_name && (
                    <div style={{ marginBottom: 14, padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>Brokerage / Company Name</div>
                      <div style={{ fontSize: 14, color: '#111827', fontWeight: 600 }}>{partner.account_company_name}</div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                        This is your firm&rsquo;s account name. Message the Doorly team to change it.
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                    {[
                      // 🔑 THE FIRM'S NAME IS NOT EDITABLE FROM ONE MEMBER'S PROFILE
                      // once the firm exists. Doorly contracts with, pays and
                      // reports to the ACCOUNT, and letting each member keep their
                      // own spelling of its name is the drift that put two Sales
                      // Agents with different sponsoring brokers on one account.
                      // It is changed in DoorlyDesk, on the firm itself.
                      ...(partner?.account_company_name ? [] : [{
                        key: 'brokerage_name', label: 'Brokerage / Company Name', placeholder: 'e.g. Keller Williams Austin' }]),
                      { key: 'brokerage_address', label: 'Brokerage Address', placeholder: '123 Main St, Austin, TX 78701' },
                      { key: 'brokerage_phone', label: 'Brokerage Phone', placeholder: '(512) 000-0000' },
                      { key: 'brokerage_ein', label: 'EIN (optional)', placeholder: 'XX-XXXXXXX' },
                    ].map(f => (
                      <div key={f.key}>
                        <label style={{ fontSize: '11px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{f.label}</label>
                        <input type="text" value={companyForm[f.key]} onChange={e => setCompanyForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder}
                          style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                      </div>
                    ))}
                  </div>
                  {companyError && (
                    <div style={{ padding: '10px 14px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: '8px', marginBottom: '12px', fontSize: '13px', color: '#ff082f', fontWeight: '600' }}>{companyError}</div>
                  )}
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button onClick={() => { setEditingCompany(false); setCompanyError(null); setCompanyForm({ brokerage_name: partner?.brokerage_name || '', brokerage_address: partner?.brokerage_address || '', brokerage_phone: partner?.brokerage_phone || '', brokerage_ein: partner?.brokerage_ein || '' }); }}
                      style={{ padding: '10px 20px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', color: '#374151' }}>Cancel</button>
                    <button onClick={handleSaveCompany} disabled={companySaving}
                      style={{ padding: '10px 24px', background: companySaving ? '#e5e7eb' : '#3a6ad9', color: companySaving ? '#9ca3af' : '#fff', border: 'none', borderRadius: '8px', cursor: companySaving ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '13px' }}>
                      {companySaving ? 'Saving...' : 'Save Company Information'}
                    </button>
                  </div>
                </div>
              )}

              {/* ── 🚨 PAYOUT DETAILS — READ-ONLY HERE, AND THAT IS THE CHANGE ──
                  This panel used to be a full editor: masked fields, an emailed
                  OTP, and a Save that PATCHed /banking. Two things were wrong
                  with it.

                  🚨 IT READ THE WRONG TABLE. Every value came from
                  partner_users.ach_* — the PERSONAL columns the one-way move
                  NULLed after copying them to partner_accounts. So it showed an
                  empty panel to every partner while five firms' real details sat
                  on the account, untouched by anything this page looked at.

                  🚨 AND IT WAS THE WRONG PERSON. Doorly pays the FIRM. Where a
                  brokerage's money goes is not an individual agent's decision,
                  any more than the firm's license or its W-9 is — so entering and
                  changing it belongs to the account portal, to the broker of
                  record. The server enforces that in accountBankingAuthority;
                  this page asks it rather than deciding for itself.

                  ⚠️ IT STILL SAYS WHETHER BANKING EXISTS AND WHO HOLDS IT. "On
                  file, managed by X" is what separates "your firm cannot be paid
                  yet" from "not yours to change" — and a partner who can see
                  neither assumes the first. Never the numbers: they cannot act on
                  them, so showing them turns one person's disclosure into three. */}
              {/* 🚨 ONLY THE PEOPLE WHO CAN ACT ON IT SEE IT. This panel rendered
                  for every partner, and for an ordinary member it is a section of
                  their own profile that describes somebody else's job and offers
                  no control — "payout details" under Company Information reads as
                  something they configure.

                  ⚠️ WHAT THEY LOSE IS NOTHING, because the fact that matters
                  reaches them where it is actionable: My Tasks carries "your
                  firm's banking details are not set up yet", says their fees are
                  held until they are, and now offers Invite broker. A status line
                  in two places is one place too many when only one of them can be
                  acted on.

                  ⚠️ canEdit IS THE SERVER'S ANSWER, so this cannot disagree with
                  who PATCH /banking would actually accept. */}
              {/* 🔑 …AND THE ACCOUNT-LEVEL HOLDER WHO MAY NOT EDIT (broker report 1102): gated on canEdit alone, the
                  "!banking.canEdit" sentence below could never appear. constants/partnerPayoutPanel decides. */}
              {showPayoutPanel(banking) && (
              <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '24px', paddingTop: '20px' }}>
                <h4 id="payout" style={{ margin: '0 0 2px', fontSize: 15, fontWeight: 700, color: '#111827', scrollMarginTop: 80 }}>Payout details</h4>
                <p style={{ margin: '0 0 14px', fontSize: 12, color: '#9ca3af' }}>
                  Doorly pays your firm, so these live on your firm&rsquo;s account
                </p>

                {banking === null ? (
                  <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading&hellip;</div>
                ) : !banking.hasAccount ? (
                  <div style={{ padding: '12px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, fontSize: 13, color: '#92400e', lineHeight: 1.6 }}>
                    Your account isn&rsquo;t linked to a firm yet, and Doorly pays the firm rather than
                    the individual. Message the Doorly team and we&rsquo;ll link it.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 4, flexShrink: 0,
                        background: banking.bankingOnFile ? '#2d6a0a' : '#d97706' }} />
                      <div style={{ fontSize: 13.5, color: '#111827', fontWeight: 600 }}>
                        {banking.bankingOnFile
                          ? <>Banking is on file for {banking.firm || 'your firm'}</>
                          : <>No banking on file for {banking.firm || 'your firm'} yet</>}
                        {banking.payeeContact && (
                          <span style={{ fontWeight: 400, color: '#6b7280' }}>
                            {' '}&middot; managed by {banking.youAreThePayeeContact ? 'you' : banking.payeeContact}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* ⚠️ THE REASON, NOT JUST THE REFUSAL. "not_account_level" and
                        "not_payee_contact" are different problems with different
                        next steps, and the server sends the sentence for each. */}
                    {!banking.canEdit && (
                      <div style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.6 }}>
                        {banking.message}
                      </div>
                    )}

                    {/* ── 🔑 THE EDITOR, FOR WHOEVER THE SERVER SAYS MAY USE IT ──
                        This page keeps the flow it always had — send a code, verify,
                        five minutes to edit. What changed is WHOSE details it edits
                        and WHO gets the form.

                        🚨 IT READS THE FIRM'S COLUMNS NOW. Every value here used to
                        come from partner_users.ach_*, the personal columns the
                        one-way move NULLed after copying them to partner_accounts —
                        so this panel showed blanks to everybody while five firms'
                        real details sat on the account it never looked at. It was
                        not stale; it was reading a different table.

                        ⚠️ AND canEdit IS THE SERVER'S ANSWER, not a role check done
                        here. accountBankingAuthority is the same function PATCH
                        /banking enforces with, so the form cannot appear for someone
                        the write path would reject — and cannot be withheld from
                        someone it would accept. */}
                    {banking.canEdit && (
                      <div style={{ marginTop: 6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <div style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.6 }}>
                            {banking.bankingOnFile
                              ? 'You manage these for your firm.'
                              : 'You can set these up for your firm.'}
                            {banking.firstTime && (
                              <span> Supplying them makes you the person who manages your firm&rsquo;s
                                payout details from now on.</span>
                            )}
                          </div>
                          <span style={{ fontSize: 12, color: otpStep === 'unlocked' ? '#2d6a0a' : '#9ca3af', fontWeight: 600, flexShrink: 0, marginLeft: 12 }}>
                            {otpStep === 'unlocked' ? 'Unlocked' : 'Locked'}
                          </span>
                        </div>

                        {otpStep !== 'unlocked' && (
                          <div>
                            {/* ⚠️ LAST 4 AND NO ROUTING NUMBER, EVER. accountBankingView
                                returns neither in full — enough to confirm it is the
                                right account, never enough to use it. */}
                            {banking.bankingOnFile && (
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                                {[['Account Holder', banking.holder], ['Bank Name', banking.bank],
                                  ['Account Type', banking.accountType ? banking.accountType.charAt(0).toUpperCase() + banking.accountType.slice(1) : null],
                                  ['Account Number', banking.accountLast4 ? `\u2022\u2022\u2022\u2022 ${banking.accountLast4}` : null],
                                ].map(([label, value]) => (
                                  <div key={label} style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 12px' }}>
                                    <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{label}</div>
                                    <div style={{ fontSize: 14, fontWeight: 600, color: value ? '#111827' : '#d1d5db', fontFamily: String(value || '').includes('\u2022') ? 'monospace' : 'inherit' }}>{value || '\u2014'}</div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {otpStep === 'locked' && (
                              <div>
                                {otpError && <div style={{ marginBottom: 12, padding: '10px 14px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: 8, fontSize: 13, color: '#ff082f', fontWeight: 600 }}>{otpError}</div>}
                                <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6b7280' }}>
                                  To {banking.bankingOnFile ? 'change these' : 'add these'}, we&rsquo;ll send a verification code to your email.
                                </p>
                                <button onClick={async () => {
                                  setOtpStep('sending'); setOtpError(null);
                                  try {
                                    const res = await axios.post(`${API_URL}/api/doorlypro/banking/request-otp`, {}, { headers: authHeaders });
                                    if (res.data.success) setOtpStep('otp_sent');
                                    else { setOtpError(res.data.error); setOtpStep('locked'); }
                                  } catch (e) { setOtpError(e.response?.data?.error || 'Failed'); setOtpStep('locked'); }
                                }} style={{ padding: '11px 22px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                                  {otpStep === 'sending' ? 'Sending...' : 'Send Verification Code'}
                                </button>
                              </div>
                            )}

                            {otpStep === 'otp_sent' && (
                              <div>
                                <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6b7280' }}>Enter the 6-digit code sent to your email.</p>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                  <input type="text" inputMode="numeric" maxLength={6} value={otpCode}
                                    onChange={e => { setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setOtpError(null); }}
                                    placeholder="000000"
                                    style={{ width: 160, padding: '12px 14px', border: `1px solid ${otpError ? '#ff082f' : '#d1d5db'}`, borderRadius: 10, fontSize: 22, fontFamily: 'monospace', letterSpacing: 6, textAlign: 'center', outline: 'none' }} />
                                  <button onClick={async () => {
                                    if (otpCode.length !== 6) return;
                                    setOtpStep('verifying'); setOtpError(null);
                                    try {
                                      const res = await axios.post(`${API_URL}/api/doorlypro/banking/verify-otp`, { code: otpCode }, { headers: authHeaders });
                                      if (res.data.success) {
                                        setEditToken(res.data.edit_token); setEditTokenExpiry(res.data.expires_at); setOtpStep('unlocked'); setOtpCode('');
                                        // ⚠️ SEEDED FROM THE FIRM'S VALUES, and the two
                                        // numbers are deliberately NOT seeded: they are
                                        // never sent to the browser, so a blank field is
                                        // the honest state rather than a masked one that
                                        // would save literal bullets.
                                        setAchForm({ ach_bank_name: banking.bank || '', ach_account_holder_name: banking.holder || '', ach_routing_number: '', ach_account_number: '', ach_account_type: banking.accountType || 'checking' });
                                      } else { setOtpError(res.data.error); setOtpStep('otp_sent'); }
                                    } catch (e) { setOtpError(e.response?.data?.error || 'Invalid code'); setOtpStep('otp_sent'); }
                                  }} disabled={otpCode.length !== 6}
                                    style={{ padding: '12px 22px', background: otpCode.length === 6 ? '#3a6ad9' : '#e5e7eb', color: otpCode.length === 6 ? '#fff' : '#9ca3af', border: 'none', borderRadius: 10, cursor: otpCode.length === 6 ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 14 }}>
                                    Verify
                                  </button>
                                </div>
                                {otpError && <div style={{ marginTop: 8, fontSize: 12, color: '#ff082f', fontWeight: 600 }}>{otpError}</div>}
                              </div>
                            )}
                          </div>
                        )}

                        {otpStep === 'unlocked' && (
                          <div>
                            <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f4fee8', border: '1px solid #d4f99a', borderRadius: 8, fontSize: 12, color: '#2d6a0a', fontWeight: 600 }}>
                              Identity verified &mdash; you have 5 minutes to update
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 16 }}>
                              <div>
                                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>Account Holder Name *</label>
                                <input value={achForm.ach_account_holder_name} onChange={e => setAchForm(p => ({ ...p, ach_account_holder_name: e.target.value }))} placeholder="Full legal name"
                                  style={{ width: '100%', padding: '11px 13px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                              </div>
                              <div>
                                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>Bank Name *</label>
                                <input value={achForm.ach_bank_name} onChange={e => setAchForm(p => ({ ...p, ach_bank_name: e.target.value }))} placeholder="e.g. Chase"
                                  style={{ width: '100%', padding: '11px 13px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
                              </div>
                              <div style={{ display: 'flex', gap: 10 }}>
                                {['checking', 'savings'].map(t => (
                                  <button key={t} type="button" onClick={() => setAchForm(p => ({ ...p, ach_account_type: t }))}
                                    style={{ flex: 1, padding: 11, border: `2px solid ${achForm.ach_account_type === t ? '#3a6ad9' : '#e5e7eb'}`, borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14, background: achForm.ach_account_type === t ? '#edf1fb' : '#fff', color: achForm.ach_account_type === t ? '#3a6ad9' : '#6b7280' }}>
                                    {t.charAt(0).toUpperCase() + t.slice(1)}
                                  </button>
                                ))}
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div>
                                  <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>Routing Number *</label>
                                  <input type="text" inputMode="numeric" maxLength={9} value={achForm.ach_routing_number}
                                    onChange={e => setAchForm(p => ({ ...p, ach_routing_number: e.target.value.replace(/\D/g, '').slice(0, 9) }))}
                                    placeholder="9 digits" style={{ width: '100%', padding: '11px 13px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 14, boxSizing: 'border-box', outline: 'none', fontFamily: 'monospace', letterSpacing: 2 }} />
                                </div>
                                <div>
                                  <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>Account Number *</label>
                                  <input type="text" inputMode="numeric" value={achForm.ach_account_number}
                                    onChange={e => setAchForm(p => ({ ...p, ach_account_number: e.target.value.replace(/\D/g, '').slice(0, 17) }))}
                                    placeholder="Account number" style={{ width: '100%', padding: '11px 13px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 14, boxSizing: 'border-box', outline: 'none', fontFamily: 'monospace', letterSpacing: 2 }} />
                                </div>
                              </div>
                            </div>
                            {achError && <div style={{ marginBottom: 14, padding: '10px 14px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: 8, fontSize: 13, color: '#ff082f', fontWeight: 600 }}>{achError}</div>}
                            {achSuccess && <div style={{ marginBottom: 14, padding: '10px 14px', background: '#f4fee8', border: '1px solid #d4f99a', borderRadius: 8, fontSize: 13, color: '#2d6a0a', fontWeight: 600 }}>Banking information updated</div>}
                            <div style={{ display: 'flex', gap: 10 }}>
                              <button onClick={() => { setOtpStep('locked'); setEditToken(null); setAchError(null); }}
                                style={{ padding: '11px 20px', background: '#f3f4f6', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, color: '#374151' }}>Cancel</button>
                              <button onClick={async () => {
                                setAchSaving(true); setAchError(null); setAchSuccess(false);
                                try {
                                  const res = await axios.patch(`${API_URL}/api/doorlypro/banking`, { ...achForm, edit_token: editToken }, { headers: authHeaders });
                                  if (res.data.success) {
                                    setAchSuccess(true); setOtpStep('locked'); setEditToken(null); setEditTokenExpiry(null);
                                    // 🚨 RE-READ THE FIRM, NOT /me. The old handler
                                    // refetched the partner — which carries the emptied
                                    // personal columns — so the panel showed the same
                                    // blanks straight after a successful save.
                                    loadBanking();
                                    setTimeout(() => setAchSuccess(false), 4000);
                                  } else setAchError(res.data.error);
                                } catch (e) { setAchError(e.response?.data?.error || 'Failed'); }
                                finally { setAchSaving(false); }
                              }} disabled={achSaving || !achForm.ach_bank_name?.trim() || !achForm.ach_account_holder_name?.trim() || achForm.ach_routing_number?.length !== 9 || !achForm.ach_account_number?.trim()}
                                style={{ flex: 1, padding: '11px 20px', background: achSaving || !achForm.ach_bank_name?.trim() || !achForm.ach_account_holder_name?.trim() || achForm.ach_routing_number?.length !== 9 || !achForm.ach_account_number?.trim() ? '#e5e7eb' : '#3a6ad9', color: achSaving ? '#9ca3af' : '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 800, fontSize: 14 }}>
                                {achSaving ? 'Saving...' : 'Save Banking Information'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 🚨 AND WHAT IT COSTS, because banking is a payout gate and a
                        partner who does not know that chases a missing payment
                        instead of their broker. */}
                    {!banking.bankingOnFile && (
                      <div style={{ fontSize: 12, color: '#b45309', lineHeight: 1.6 }}>
                        Partner fees you earn are held until your firm&rsquo;s payout details are on
                        file &mdash; held, not cancelled, and released as soon as they are.
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}
            </div>
            )}

            {/* Account & Security — change password / change email */}
            <div style={{ marginTop: '20px', background: '#fff', border: '1px solid #f0f0f0', borderRadius: '14px', padding: '20px' }}>
              <h4 style={{ margin: '0 0 2px', fontSize: 15, fontWeight: 700, color: '#111827' }}>Account & Security</h4>
              <p style={{ margin: '0 0 16px', fontSize: 12, color: '#9ca3af' }}>Update the email and password you use to sign in</p>

              {(() => {
                const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit', marginBottom: 10 };
                const labelStyle = { fontSize: '11px', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' };
                const btnStyle = (disabled) => ({ padding: '10px 20px', background: disabled ? '#9db8f0' : '#3a6ad9', color: '#fff', border: 'none', borderRadius: '8px', cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '13px' });
                const msgBox = (m) => m && (
                  <div style={{ margin: '4px 0 12px', padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                    background: m.ok ? '#f0fdf4' : '#fff0f3', border: `1px solid ${m.ok ? '#bbf7d0' : '#fecdd3'}`, color: m.ok ? '#166534' : '#ff082f' }}>{m.text}</div>
                );
                return (
                  <>
                    {/* Change Password */}
                    <div style={{ marginBottom: 24 }}>
                      <div style={{ ...labelStyle, marginBottom: 10, color: '#374151' }}>Change Password</div>
                      <label style={labelStyle}>Current Password</label>
                      <input type="password" autoComplete="current-password" value={pwForm.current_password}
                        onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))} style={inputStyle} />
                      <label style={labelStyle}>New Password</label>
                      <input type="password" autoComplete="new-password" value={pwForm.new_password}
                        onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))} placeholder="At least 8 characters" style={inputStyle} />
                      <label style={labelStyle}>Confirm New Password</label>
                      <input type="password" autoComplete="new-password" value={pwForm.confirm_password}
                        onChange={e => setPwForm(p => ({ ...p, confirm_password: e.target.value }))} style={inputStyle} />
                      {msgBox(pwMsg)}
                      <button onClick={handleChangePassword} disabled={pwSaving} style={btnStyle(pwSaving)}>
                        {pwSaving ? 'Saving…' : 'Update Password'}
                      </button>
                    </div>

                    {/* Change Email */}
                    <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 20 }}>
                      <div style={{ ...labelStyle, marginBottom: 4, color: '#374151' }}>Change Login Email</div>
                      <p style={{ margin: '0 0 10px', fontSize: 12, color: '#9ca3af' }}>
                        Current: <strong style={{ color: '#374151' }}>{partner?.email}</strong>. We'll email a confirmation link to the new address; your login changes only after you confirm it.
                      </p>
                      <label style={labelStyle}>New Email</label>
                      <input type="email" autoComplete="email" value={emailForm.new_email}
                        onChange={e => setEmailForm(p => ({ ...p, new_email: e.target.value }))} style={inputStyle} />
                      <label style={labelStyle}>Current Password</label>
                      <input type="password" autoComplete="current-password" value={emailForm.current_password}
                        onChange={e => setEmailForm(p => ({ ...p, current_password: e.target.value }))} style={inputStyle} />
                      {msgBox(emailMsg)}
                      <button onClick={handleChangeEmail} disabled={emailSaving} style={btnStyle(emailSaving)}>
                        {emailSaving ? 'Sending…' : 'Send Confirmation Link'}
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Sign out */}
            <div style={{ marginTop: '20px' }}>
              <button onClick={() => { partnerLogout(); navigate('/doorlypro/login'); }}
                style={{ width: '100%', padding: '12px', background: '#fff', color: '#ff082f', border: '1px solid #fecdd3', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Icon name="logout" size={16} color="#ff082f" /> Sign Out
              </button>
            </div>
          </>
          )}
        </div>
      </DoorlyProLayout>
    </RequirePartnerAuth>
  );
}
